import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Box, CircularProgress } from '@mui/material';
import { columnType, dataType, Order, OrderBy, tableState, initialTableState } from './types';
import { handleRequestSort } from './sorts';
import BasicTableBody from './sections/TableBody';
import BasicTableHeader from './sections/TableHeader';

export interface IBaseTableProps {
	data: dataType[];
	columns: columnType[];
	rowAction?: (row: dataType) => void;
	rowsClass?: string;
	tableClass?: string;
	defaultSortBy?: string;
	defaultOrderBy?: Order;
	hideHeader?: boolean;
	noShrink?: boolean;
	dateFormat?: string;
	isLoading?: boolean;
	tableState?: tableState;
	setTableState?: React.Dispatch<React.SetStateAction<tableState>>;
	getRowStyle?: (row: dataType) => React.CSSProperties | undefined;
}

export const BaseTable: React.FC<IBaseTableProps> = ({
	data,
	columns,
	rowAction,
	rowsClass,
	tableClass,
	defaultSortBy,
	defaultOrderBy,
	hideHeader,
	noShrink,
	dateFormat,
	isLoading = false,
	tableState: externalTableState,
	setTableState: externalSetTableState,
	getRowStyle,
}) => {
	const [internalTableState, setInternalTableState] = useState<tableState>({
		...initialTableState,
		sortBy: defaultSortBy || '',
		orderBy: defaultOrderBy || (OrderBy.DESENDING as Order),
	});

	const tableStateToUse = externalTableState || internalTableState;
	const setTableStateToUse = externalSetTableState || setInternalTableState;

	return (
		<Box sx={{ position: 'relative' }}>
			<TableContainer component={Paper} sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
				<Table className={`BasicTable ${tableClass || ''}`}>
					{!hideHeader && (
						<BasicTableHeader
							headCells={columns}
							tableState={tableStateToUse}
							setState={setTableStateToUse}
							order={tableStateToUse.orderBy}
							orderBy={tableStateToUse.sortBy || ''}
							onRequestSort={handleRequestSort}
							noShrink={noShrink}
							data={data}
						/>
					)}
					<BasicTableBody
						dateFormat={dateFormat}
						data={data}
						columns={columns}
						tableState={tableStateToUse}
						hideHeader={hideHeader}
						rowsClass={rowsClass}
						noShrink={noShrink}
						rowAction={rowAction}
						getRowStyle={getRowStyle}
					/>
				</Table>
			</TableContainer>
			{isLoading && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						pointerEvents: 'none',
					}}
				>
					<CircularProgress size={32} />
				</Box>
			)}
		</Box>
	);
};

export default BaseTable;
